import React, { useState } from "react";
import { Alert, Button, Stack, TextField } from "@mui/material";
import { GetTextFieldStyle } from "lib/styles/textField";

type props = {
	value: string;
	setValue: (v: string) => void;
	disabled: boolean;
	editing: boolean;
	onClearError?: () => void;
	onCheckGMID: () => Promise<{ ok: boolean; msg: string }>;
};

export function GMIDField({
	value,
	setValue,
	disabled,
	editing,
	onClearError,
	onCheckGMID,
}: props) {
	const [gmidChecked, setChecked] = useState(!!editing);
	const [gmidValid, setValid] = useState(!!editing);
	const [gmidMessage, setMessage] = useState("");
	const [checking, setChecking] = useState(false);
	const [formatError, setFormatError] = useState<string>("");

	const denseFieldSx = {
		"height": 40,
		"& .MuiInputBase-input": { py: 1 },
		"& .MuiSelect-select": { py: 1 },
	} as const;

	const validateFormat = (raw: string) => {
		if (!raw) return "GMID is required";
		const trimmed = raw.trim();
		if (trimmed.length !== 6) return "GMID must be exactly 6 characters";
		if (!/^[A-Za-z0-9]+$/.test(trimmed))
			return "GMID must be alphanumeric only";
		return "";
	};

	const doCheck = async () => {
		if (disabled) return;
		onClearError?.();
		setMessage("");
		setChecked(false);
		setValid(false);
		const fe = validateFormat(value || "");
		if (fe) {
			setFormatError(fe);
			return;
		}
		setFormatError("");
		setChecking(true);
		try {
			const res = await onCheckGMID();
			setChecked(true);
			setValid(res.ok);
			setMessage(res.msg);
		} finally {
			setChecking(false);
		}
	};

	return (
		<>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{ pt: "10px", flexWrap: "wrap" }}
			>
				<TextField
					label="GMID"
					size="small"
					value={value || ""}
					onChange={(e) => {
						if (disabled) return;
						onClearError?.();
						setMessage("");
						setChecked(false);
						setValid(false);
						const next = e.target.value;
						setValue(next);
						const fe = validateFormat(next);
						setFormatError(fe);
					}}
					disabled={editing || disabled}
					fullWidth
					required
					sx={{ ...GetTextFieldStyle(), flex: 1, minWidth: 260 }}
					InputProps={{ sx: denseFieldSx }}
					error={!!formatError && !editing}
					helperText={
						!editing && formatError ? formatError : undefined
					}
				/>
				{!editing && (
					<Button
						variant="outlined"
						size="small"
						onClick={doCheck}
						disabled={
							checking ||
							disabled ||
							!!formatError ||
							!(value || "").trim()
						}
						sx={{
							height: 36,
							whiteSpace: "nowrap",
							mt: { xs: 1, sm: 0 },
						}}
					>
						{checking ? "Finding..." : "Find User"}
					</Button>
				)}
			</Stack>
			{!editing && gmidChecked && (
				<Alert severity={gmidValid ? "success" : "warning"}>
					{gmidMessage}
				</Alert>
			)}
		</>
	);
}
